import { axiosInstance } from "@/lib/axios-instance";
import type {
  AnalysisTemplate,
  BuiltinAnalysisTemplate,
} from "@/api/deals-api";

/**
 * Generated analysis documents on a deal.
 *
 * Separate from the analysis *templates* API (in deals-api, alongside the other
 * workspace config): a template is something a fund defines once, a document is
 * produced per deal and edited afterwards.
 */

export type AnalysisSectionStatus = "pending" | "running" | "done" | "error";

export interface AnalysisDocumentSection {
  /** Ties back to the template section. Survives the template being edited. */
  section_id: string;
  title: string;
  html: string;
  status: AnalysisSectionStatus;
  generated_at?: string;
  /**
   * Set once a person has edited the section. Kept alongside `generated_at`,
   * and the reason a full regenerate skips this section unless forced.
   */
  edited_at?: string;
  error?: string;
  /** The template dropped this section. Content kept, no longer regenerated. */
  orphaned?: boolean;
  /**
   * Where this section's claims came from — the data-room passages the worker
   * retrieved and the model reported drawing on (SAAS-921).
   *
   * Section-level rather than inline markers in the prose. That is what makes
   * a citation impossible to destroy by editing the paragraph around it: it
   * was never in the paragraph.
   */
  sources?: AnalysisSectionSource[];
}

export interface AnalysisSectionSource {
  file_url?: string;
  file_type?: string;
  source_type?: string;
  /**
   * A number when the pipeline cites a bare page or slide, as `IMetricSource`
   * and the report components have always allowed.
   */
  source_description?: string | number;
  created_on?: string;
}

export interface AnalysisDocument {
  _id: string;
  opportunity_application_id: string;
  company_id: string;
  opportunity_id: string;
  template_id: string;
  /** Denormalised, so a document still reads correctly after a rename. */
  template_name: string;
  template_icon?: string;
  sections: AnalysisDocumentSection[];
  created_at: string;
  updated_at: string;
}

export interface AnalysisDocumentsResponse {
  documents: AnalysisDocument[];
  templates: AnalysisTemplate[];
  builtins: BuiltinAnalysisTemplate[];
}

export interface GenerateAnalysisResponse {
  document: AnalysisDocument;
  queued: number;
  /**
   * Every section was skipped because a person had edited it. Distinct from
   * "nothing to do" — the caller should offer to overwrite rather than looking
   * like the button did nothing.
   */
  skipped_edited?: boolean;
}

export const analysisDocumentsApi = {
  list: async (dealId: string): Promise<AnalysisDocumentsResponse> => {
    const response = await axiosInstance.get("/analysis_documents", {
      params: { deal_id: dealId },
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return {
      documents: (response.data.documents ?? []) as AnalysisDocument[],
      templates: (response.data.templates ?? []) as AnalysisTemplate[],
      builtins: (response.data.builtins ?? []) as BuiltinAnalysisTemplate[],
    };
  },

  /**
   * Start (or restart) a generation.
   *
   * `sectionId` regenerates exactly that section. Without it every section is
   * queued, except ones a person has edited — pass `force` to include those too.
   */
  generate: async (params: {
    dealId: string;
    templateId: string;
    sectionId?: string;
    force?: boolean;
  }): Promise<GenerateAnalysisResponse> => {
    const response = await axiosInstance.post("/generate_analysis", {
      deal_id: params.dealId,
      template_id: params.templateId,
      section_id: params.sectionId,
      force: params.force,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return {
      document: response.data.document as AnalysisDocument,
      queued: Number(response.data.queued ?? 0),
      skipped_edited: Boolean(response.data.skipped_edited),
    };
  },

  saveSection: async (params: {
    documentId: string;
    sectionId: string;
    html: string;
  }): Promise<void> => {
    const response = await axiosInstance.post("/update_analysis_section", {
      document_id: params.documentId,
      section_id: params.sectionId,
      html: params.html,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
  },

  /**
   * Ask for a revision of one selected passage.
   *
   * Returns the proposal and writes nothing. The caller shows it as a diff, and
   * an accepted revision is saved through `saveSection` like any other edit —
   * which is what stops the AI editing a memo behind its author's back.
   */
  reviseSelection: async (params: {
    documentId: string;
    sectionId: string;
    instruction: string;
    /** The selected passage, as HTML. */
    passage: string;
    /** The rest of the document, so the revision fits what surrounds it. */
    documentContext?: string;
  }): Promise<string> => {
    const response = await axiosInstance.post("/revise_analysis_selection", {
      document_id: params.documentId,
      section_id: params.sectionId,
      instruction: params.instruction,
      passage: params.passage,
      document_context: params.documentContext,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
    return String(response.data.html ?? "");
  },

  remove: async (documentId: string): Promise<void> => {
    const response = await axiosInstance.post("/delete_analysis_document", {
      document_id: documentId,
    });
    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }
  },
};
